import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from 'state/axios';
import { format } from "date-fns";
import {
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';
import DeleteConfirm from "components/DeleteConfirm";
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const TravelAllowances = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "TravelAllowances", "create", false);
  const canEdit   = hasPermission(user?.permissions, "TravelAllowances", "update", false);
  const canDelete = hasPermission(user?.permissions, "TravelAllowances", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [travelAllowances, setTravelAllowances] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  
  useEffect(() => {
    const fetchTravelAllowances = async () => {
      try {
        setLoading(true);
        const res = await api.get('/travelallowances');
        setTravelAllowances(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTravelAllowances();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      width: 60,
      renderCell: (params) =>
        params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "travelAllowanceCode",
      headerName: "TravelAllowanceCode",
      width: 150,
    },
    {
      field: "travelAllowanceName",
      headerName: "TravelAllowanceName",
      width: 260,
    },    
    {
      field: "variableName",
      headerName: "VariableName",
      width: 100,      
    },
    {
      field: "applicableTo",
      headerName: "ApplicableTo",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.applicableTo || [];
        if (!list.length) return "";

        return (
          <Box
            sx={{
              height: '100%',
              display: "flex",
              flexDirection: "column",
              overflowY: 'auto',
              overflowX: 'hidden',
              gap: 0.5,
              pr: 1,
            }}
          >
            {list.map((item, index) => (
              <Box
                key={index}
                display="grid"
                gridTemplateColumns="120px 1fr auto"
                alignItems="center"
                columnGap={1}
                
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "primary.main",
                    whiteSpace: "normal",     // cho phép xuống dòng
                    wordBreak: "break-word",  // tự ngắt từ khi cần
                    overflowWrap: "anywhere"  // fallback an toàn
                  }}
                >
                  {Array.isArray(item.jobGrade)
                    ? item.jobGrade.join(", ")
                    : item.jobGrade}
                </Typography>

                <Typography variant="body2" sx={{ textAlign: "right" }}>
                  {new Intl.NumberFormat("vi-VN").format(item.value)}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    borderRadius: 1,
                    px: 0.5,
                    textAlign: "center",
                    minWidth: 32,
                  }}
                >
                  {item.currency}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "effectiveFrom",
      headerName: "EffectiveFrom",
      width: 100,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "effectiveTo",
      headerName: "EffectiveTo",
      width: 100,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      width: 90,
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      width: 90,
      align: "right",
    },
  ], []);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,
      sortable: false,
      width: 90,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: "100%", height: "100%" }}
          >            
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}                
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/travelallowances/${params.row._id}`)
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);                  
                }}
              >
                <DeleteForeverIcon />
              </IconButton>                
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType("single");
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, travelAllowances);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }

    setDeleteType("bulk");
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === "single") {
        if (!idToDelete) return;
        await api.delete(
          `/travelallowances/delete/${idToDelete}`
        );
        setTravelAllowances(
          travelAllowances.filter((item) => item._id !== idToDelete)
        );
        toast.success("Cost item deleted successfully.");
      } else if (deleteType === "bulk") {
        const selectedIds = getSelectedIds(rowSelectionModel, travelAllowances);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/travelallowances/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setTravelAllowances(travelAllowances.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete cost items!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    travelAllowances,
  ]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Travel Allowances"
          subtitle={`${loading ? "Loading..." : "List of Travel Allowances"}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, travelAllowances)}
                    onClick={handleBulkDelete}
                  >
                    <DeleteIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/travelallowances/new")}>
                    <AddCircleOutlineIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <Typography color="error">Error: {error?.message || ''}</Typography>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12" }}    
          >                            
            {isMobile ? (
              <CardList
                rows={travelAllowances}
                primaryField="travelAllowanceName"
                fields={[
                  { label: "Code", key: "travelAllowanceCode" },
                  { label: "Variable", key: "variableName" },
                  { label: "Effective From", key: "effectiveFrom" },
                  { label: "Effective To", key: "effectiveTo" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/travelallowances/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={travelAllowances || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default TravelAllowances;